import type { AdminConfigItemDto, JsonValue } from '@carcheck/shared';
import { useState, type ReactElement } from 'react';

import { DealStagesForm } from './DealStagesForm';
import { FxForm } from './FxForm';
import type { ConfigFormKind } from '../_lib/configForms';
import { asRawRecords, isRawRecord, toJsonValue } from '../_lib/rawRecord';
import type { SetConfigOutcome } from '../_lib/useSetConfig';

/**
 * AUT-542 — the form row of a `/config` key (`deals.stages` /
 * `specialists.fx`). Sends the edited raw copy through the page's
 * `onSave`; a 400 message is shown under the form, success closes it.
 */
export interface ConfigKeyFormProps {
  item: AdminConfigItemDto;
  kind: ConfigFormKind;
  onSave: (variables: { key: string; value: JsonValue }) => Promise<SetConfigOutcome>;
  busy: boolean;
  onClose: () => void;
  onEditJson: () => void;
}

export const ConfigKeyForm = ({
  item,
  kind,
  onSave,
  busy,
  onClose,
  onEditJson,
}: ConfigKeyFormProps): ReactElement => {
  const [error, setError] = useState<string | undefined>(undefined);

  const submit = async (value: JsonValue): Promise<void> => {
    setError(undefined);
    const outcome = await onSave({ key: item.key, value });
    if (outcome.ok) onClose();
    else if (outcome.fieldError) setError(outcome.fieldError);
  };

  if (kind === 'stages') {
    return (
      <DealStagesForm
        initial={asRawRecords(item.value) ?? []}
        busy={busy}
        error={error}
        onSave={(raw) => void submit(toJsonValue(raw))}
        onCancel={onClose}
        onEditJson={onEditJson}
      />
    );
  }

  return (
    <FxForm
      stored={isRawRecord(item.value) ? item.value : null}
      busy={busy}
      error={error}
      onSave={(record) => void submit(toJsonValue(record))}
      onCancel={onClose}
    />
  );
};
